// src/components/boq/ItemPickerModal.jsx - NO ESLINT WARNINGS
import React, { useState, useEffect, useCallback } from 'react';
import { X, Search, Package, FolderOpen, Folder, ChevronRight, ChevronDown, CheckCircle } from 'lucide-react';
import itemService from '../../services/itemService';

const ItemPickerModal = ({ isOpen, onClose, onSelect, filterType = null }) => {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [selectedItem, setSelectedItem] = useState(null);

  // Filter tree recursively by item type
// ✅ FIXED: Handle 'ALL' type properly
const filterTreeByType = useCallback((itemList, type) => {
  // If type is 'ALL' or null/undefined, return all items
  if (!type || type === 'ALL') {
    console.log('✅ No filtering needed (type is ALL)');
    return itemList;
  }
  
  console.log('🔍 Filtering by type:', type);
  
  return itemList
    .map(item => {
      // If it's a group, keep it and filter children
      if (item.isGroup) {
        const filteredChildren = item.children ? filterTreeByType(item.children, type) : [];
        // Only keep group if it has matching children
        if (filteredChildren.length > 0) {
          return {
            ...item,
            children: filteredChildren
          };
        }
        return null;
      }
      
      // If it's a leaf item, check if type matches
      // Allow items with no itemType or matching itemType
      if (!item.itemType || item.itemType === type) {
        return item;
      }
      
      return null;
    })
    .filter(item => item !== null);
}, []);

  // Fetch items with useCallback
// Fetch items with proper logging
const fetchItems = useCallback(async () => {
  setIsLoading(true);
  try {
    console.log('🔄 Fetching items...');
    console.log('filterType:', filterType);
    
    const data = await itemService.getItemTree();
    
    console.log('✅ Items received:', data);
    console.log('Total items:', data?.length);
    
    if (!data || !Array.isArray(data)) {
      console.error('❌ Invalid data:', data);
      setItems([]);
      return;
    }
    
    // Filter by type if specified (and not 'ALL')
    let filteredData = data;
    if (filterType && filterType !== 'ALL') {
      console.log('🔍 Filtering by type:', filterType);
      filteredData = filterTreeByType(data, filterType);
      console.log('✅ After filter:', filteredData.length, 'items');
    } else {
      console.log('✅ No filtering (showing all items)');
    }
    
    console.log('✅ Final items to display:', filteredData);
    setItems(filteredData);
    
    // Auto-expand root items
    if (filteredData.length > 0) {
      const rootIds = filteredData.map(item => item.id.toString());
      setExpandedItems(new Set(rootIds));
      console.log('✅ Auto-expanded', rootIds.length, 'root items');
    }
    
  } catch (error) {
    console.error('❌ Error:', error);
    console.error('Details:', error.response?.data);
  } finally {
    setIsLoading(false);
  }
}, [filterType, filterTreeByType]);

  // Fetch items on mount
  useEffect(() => {
    if (isOpen) {
      fetchItems();
    }
  }, [isOpen, fetchItems]);

  // Filter items by search query
  const filterItemsBySearch = (itemList) => {
    if (!searchQuery) return itemList;
    
    const query = searchQuery.toLowerCase();
    
    return itemList.filter(item => {
      const matchesSearch = 
        item.itemNumber?.toLowerCase().includes(query) ||
        (item.itemCode && item.itemCode.toLowerCase().includes(query)) ||
        item.itemName?.toLowerCase().includes(query);
      
      const hasMatchingChildren = 
        item.children && filterItemsBySearch(item.children).length > 0;
      
      return matchesSearch || hasMatchingChildren;
    }).map(item => ({
      ...item,
      children: item.children ? filterItemsBySearch(item.children) : []
    }));
  };

  const filteredItems = filterItemsBySearch(items);

  // Toggle expand/collapse
  const toggleExpand = (itemId) => {
    const newExpanded = new Set(expandedItems);
    const idStr = itemId.toString();
    
    if (newExpanded.has(idStr)) {
      newExpanded.delete(idStr);
    } else {
      newExpanded.add(idStr);
    }
    setExpandedItems(newExpanded);
  };

  // Handle item selection
  const handleItemSelect = (item) => {
    console.log('🎯 Item clicked:', item);
    
    // Can only select actual items (not groups)
    if (!item.isGroup && item.isActive) {
      console.log('✅ Item selected:', item);
      setSelectedItem(item);
    } else {
      console.log('⚠️ Cannot select:', {
        isGroup: item.isGroup,
        isActive: item.isActive
      });
    }
  };

  // Handle confirm selection
  const handleConfirm = () => {
    if (selectedItem) {
      console.log('✅ Confirming selection:', selectedItem);
      onSelect(selectedItem);
      handleClose();
    }
  };

  // Handle close
  const handleClose = () => {
    setSearchQuery('');
    setSelectedItem(null);
    onClose();
  };

  // Format currency
  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(value);
  };

  // Render tree item
  const renderTreeItem = (item, depth = 0) => {
    const isExpanded = expandedItems.has(item.id.toString());
    const hasChildren = item.children && item.children.length > 0;
    const indentWidth = depth * 20;
    const isSelected = selectedItem && selectedItem.id === item.id;
    const canSelect = !item.isGroup && item.isActive;

    return (
      <React.Fragment key={item.id}>
        <div
          className={`flex items-center gap-2 px-3 py-2 cursor-pointer transition-colors ${
            isSelected ? 'bg-red-100 border-l-4 border-red-600' : 
            canSelect ? 'hover:bg-gray-50' : 'opacity-60'
          }`}
          style={{ paddingLeft: `${12 + indentWidth}px` }}
          onClick={() => handleItemSelect(item)}
        >
          {hasChildren ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleExpand(item.id);
              }}
              className="p-0.5 hover:bg-gray-200 rounded"
            >
              {isExpanded ? (
                <ChevronDown className="w-4 h-4 text-gray-600" />
              ) : (
                <ChevronRight className="w-4 h-4 text-gray-600" />
              )}
            </button>
          ) : (
            <div className="w-5" />
          )}

          {item.isGroup ? (
            isExpanded ? (
              <FolderOpen className="w-4 h-4 text-yellow-500 flex-shrink-0" />
            ) : (
              <Folder className="w-4 h-4 text-yellow-600 flex-shrink-0" />
            )
          ) : (
            <Package className="w-4 h-4 text-blue-600 flex-shrink-0" />
          )}

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs text-gray-600">
                {item.itemCode || item.itemNumber}
              </span>
              <span className={`text-sm truncate ${
                item.isGroup ? 'font-semibold text-gray-900' : 'text-gray-800'
              }`}>
                {item.itemName}
              </span>
              {!item.isActive && (
                <span className="text-xs px-2 py-0.5 rounded bg-gray-200 text-gray-600">
                  INACTIVE
                </span>
              )}
            </div>
            {!item.isGroup && (
              <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500">
                <span>{item.unit}</span>
                <span>•</span>
                <span className="font-medium text-gray-700">
                  {formatCurrency(item.defaultPrice)}
                </span>
              </div>
            )}
          </div>

          {isSelected && (
            <CheckCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          )}
        </div>

        {hasChildren && isExpanded && item.children.map(child => 
          renderTreeItem(child, depth + 1)
        )}
      </React.Fragment>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-500 text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Package className="w-6 h-6" />
            <div>
              <h2 className="text-xl font-bold">Select Item</h2>
              <p className="text-blue-100 text-sm mt-0.5">
                {filterType ? `${filterType} items only` : 'Choose an item from master data'}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-blue-700 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by code or name..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Item Tree */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="flex flex-col items-center gap-3">
                <div className="flex space-x-2">
                  <div className="w-3 h-3 bg-blue-400 rounded-full animate-bounce"></div>
                  <div className="w-3 h-3 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                  <div className="w-3 h-3 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                </div>
                <p className="text-gray-500 text-sm">Loading items...</p>
              </div>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-sm">No items found</p>
                <p className="text-gray-400 text-xs mt-1">
                  {searchQuery ? 'Try adjusting your search' : 'No items available'}
                </p>
              </div>
            </div>
          ) : (
            <div className="py-2">
              {filteredItems.map(item => renderTreeItem(item))}
            </div>
          )}
        </div>

        {/* Selected Item Info */}
        {selectedItem && (
          <div className="border-t border-gray-200 bg-blue-50 p-4">
            <div className="flex items-start gap-3">
              <Package className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-semibold text-gray-900">Selected Item:</div>
                <div className="mt-1 text-sm">
                  <span className="font-mono text-gray-600">{selectedItem.itemCode || selectedItem.itemNumber}</span>
                  {' - '}
                  <span className="text-gray-800">{selectedItem.itemName}</span>
                </div>
                <div className="mt-1 flex items-center gap-3 text-xs text-gray-600">
                  <span>Unit: <span className="font-medium">{selectedItem.unit}</span></span>
                  <span>•</span>
                  <span>Default Price: <span className="font-medium">{formatCurrency(selectedItem.defaultPrice)}</span></span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
          <button
            onClick={handleClose}
            className="px-6 py-2 border border-gray-300 text-gray-700 hover:bg-gray-100 font-medium rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedItem}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 py-2 rounded-lg transition-colors shadow-md hover:shadow-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <CheckCircle className="w-5 h-5" />
            Select Item
          </button>
        </div>
      </div>
    </div>
  );
};

export default ItemPickerModal;